// ABOUTME: Admin settings page component for managing system-wide settings
// ABOUTME: Currently handles RequireAdminJustification toggle with Material UI components

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import api from '../services/api';
import AdminDevelopmentalAverages from './AdminDevelopmentalAverages';
import { AdminMakeItRightDefaults } from './AdminMakeItRightDefaults';
import { AdminResetAndReflectDefaults } from './AdminResetAndReflectDefaults';
import { AdminOneOnOneTimeDefaults } from './AdminOneOnOneTimeDefaults';
import { invalidateSiteSettingsCache } from '../hooks/useSiteSettings';

interface AdminSettingsData {
  requireAdminJustification: boolean;
  defaultDailyLoggingGoalPerChild?: number;
  requireEmailVerification?: boolean;
  defaultThemeName?: string;
}

const AdminSettings: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<AdminSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get<AdminSettingsData>('/api/admin/settings');
        setSettings(response.data);
        setError(null);
      } catch (err: unknown) {
        console.error('Failed to fetch admin settings:', err);
        setError('Failed to load admin settings. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleToggleChange = async (setting: keyof AdminSettingsData, value: boolean) => {
    if (!settings) return;

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updateDto = { ...settings, [setting]: value };
      const response = await api.put<AdminSettingsData>('/api/admin/settings', updateDto);

      setSettings(response.data);
      setSuccessMessage('Settings updated successfully');

      // Invalidate site settings cache so changes are reflected immediately
      if (setting === 'requireEmailVerification') {
        invalidateSiteSettingsCache();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to update settings:', err);
      setError('Failed to update settings. Please try again.');
      
      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleThemeChange = async (value: string) => {
    if (!settings) return;

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updateDto = { ...settings, defaultThemeName: value };
      const response = await api.put<AdminSettingsData>('/api/admin/settings', updateDto);

      setSettings(response.data);
      setSuccessMessage('Settings updated successfully');

      // Default theme affects the public /api/sitesettings response too
      invalidateSiteSettingsCache();

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to update settings:', err);
      setError('Failed to update settings. Please try again.');

      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleNumberChange = async (setting: keyof AdminSettingsData, value: number | null) => {
    if (!settings) return;
    
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updateDto = { ...settings, [setting]: value };
      const response = await api.put<AdminSettingsData>('/api/admin/settings', updateDto);
      
      setSettings(response.data);
      setSuccessMessage('Settings updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to update settings:', err);
      setError('Failed to update settings. Please try again.');
      
      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load admin settings. Please refresh the page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header with back button */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin')}
          sx={{ mr: 2 }}
        >
          Back to Admin
        </Button>
        <Typography variant="h4" component="h1">
          Admin Settings
        </Typography>
      </Box>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Configure system-wide administrative settings and security policies.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            Security Settings
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            Control access and authorization requirements for administrative functions.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={settings.requireAdminJustification}
                onChange={(e) => handleToggleChange('requireAdminJustification', e.target.checked)}
                disabled={saving}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" component="div">
                  Require Admin Justification
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  When enabled, administrators must provide a reason when accessing
                  cross-family data. This creates an audit trail for compliance
                  purposes (COPPA/FERPA).
                </Typography>
              </Box>
            }
            disabled={saving}
          />

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={settings.requireEmailVerification ?? true}
                onChange={(e) => handleToggleChange('requireEmailVerification', e.target.checked)}
                disabled={saving}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" component="div">
                  Require Email Verification
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  When enabled, users must verify their email within 7 days of registration.
                  After 7 days, unverified users will be blocked from accessing the application.
                  When disabled, all unverified users are immediately marked as &quot;verification skipped&quot;
                  and can access the application without restriction.
                </Typography>
              </Box>
            }
            disabled={saving}
          />

          {saving && (
            <Box display="flex" alignItems="center" mt={2}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Updating settings...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Daily Logging Goals Configuration */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            Daily Logging Goals
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure the default daily logging goal per child across all families. 
            Families can override this with their own goal in their settings.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ maxWidth: 300 }}>
            <TextField
              label="Default Daily Logging Goal Per Child"
              type="number"
              value={settings?.defaultDailyLoggingGoalPerChild || 10}
              onChange={(e) => {
                const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                if (value === null || value >= 0) {
                  handleNumberChange('defaultDailyLoggingGoalPerChild', value);
                }
              }}
              disabled={saving}
              fullWidth
              variant="outlined"
              inputProps={{ min: 0, step: 1 }}
              helperText="Number of behaviors that should be logged per child per day (0 or positive integer)"
            />
          </Box>

          {saving && (
            <Box display="flex" alignItems="center" mt={2}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Updating settings...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Default Theme Configuration */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            Default Theme
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            Configure the default theme for families that haven&apos;t set their own, and for
            unauthenticated pages. Families can override this in their own settings.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ maxWidth: 300 }}>
            <FormControl fullWidth>
              <InputLabel id="default-theme-selector-label">Default Theme</InputLabel>
              <Select
                labelId="default-theme-selector-label"
                value={settings?.defaultThemeName || 'default'}
                onChange={(e) => handleThemeChange(e.target.value)}
                label="Default Theme"
                disabled={saving}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="alt">Alt</MenuItem>
                <MenuItem value="nalu-v1">Nalu</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {saving && (
            <Box display="flex" alignItems="center" mt={2}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Updating settings...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Developmental Averages Configuration */}
      <Box sx={{ mt: 3 }}>
        <AdminDevelopmentalAverages />
      </Box>

      {/* Make It Right Defaults Configuration */}
      <Box sx={{ mt: 3 }}>
        <AdminMakeItRightDefaults />
      </Box>

      {/* Reset and Reflect Defaults Configuration */}
      <Box sx={{ mt: 3 }}>
        <AdminResetAndReflectDefaults />
      </Box>

      {/* 1:1 Time Defaults Configuration */}
      <Box sx={{ mt: 3 }}>
        <AdminOneOnOneTimeDefaults />
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> These settings are applied immediately but only persist 
          in memory. For permanent changes across application restarts, update your 
          configuration files or environment variables.
        </Typography>
      </Alert>
    </Box>
  );
};

export default AdminSettings;